import React from 'react';
import { WebsiteDesignProps } from './types';
import { Button } from "@/components/ui/button";
import { Search, Menu,  Sprout, Globe, Users, ArrowRight } from 'lucide-react';

const MichiganState: React.FC<WebsiteDesignProps> = ({ previewData }) => {
    const schoolName = previewData?.name || "Michigan State";

    return (
        <div className="min-h-screen bg-white font-sans text-[#18453B] selection:bg-[#18453B] selection:text-white">
            {/* Top Bar - Green */}
            <div className="bg-[#18453B] text-white py-1 px-6 flex justify-end gap-6 text-[10px] font-bold uppercase tracking-widest">
                <a href="#" className="hover:text-gray-300">Spartans</a>
                <a href="#" className="hover:text-gray-300">Directory</a>
                <a href="#" className="hover:text-gray-300">Search</a>
            </div>

            {/* Nav */}
            <nav className="bg-white py-6 px-6 md:px-12 border-t-4 border-[#18453B]">
                <div className="max-w-[1240px] mx-auto flex justify-between items-center">
                    <div className="flex items-center gap-3">
                        <div className="bg-[#18453B] text-white font-black text-xl p-2 rounded-sm">{schoolName.split(' ').map(s=>s[0]).join('')}</div>
                        <div className="flex flex-col">
                            <span className="font-serif text-2xl font-bold text-[#18453B] uppercase tracking-wide leading-none">{schoolName}</span>
                            <span className="font-serif text-xs font-bold text-[#18453B] uppercase tracking-wider leading-none">University</span>
                        </div>
                    </div>

                    <div className="hidden lg:flex gap-8 font-bold text-[#18453B] text-sm uppercase tracking-wide">
                        <a href="#" className="hover:underline underline-offset-4 decoration-2">Admissions</a>
                        <a href="#" className="hover:underline underline-offset-4 decoration-2">Academics</a>
                        <a href="#" className="hover:underline underline-offset-4 decoration-2">Research</a>
                        <a href="#" className="hover:underline underline-offset-4 decoration-2">Life at MSU</a>
                        <a href="#" className="hover:underline underline-offset-4 decoration-2">Athletics</a>
                    </div>
                    
                    <div className="flex gap-4 items-center">
                         <Search className="cursor-pointer text-[#18453B]" size={22} />
                         <Menu className="cursor-pointer lg:hidden text-[#18453B]" size={24} />
                    </div>
                </div>
            </nav>

            {/* Hero - Spartans Will */}
            <header className="relative h-[650px]">
                <img 
                    src="https://images.unsplash.com/photo-1565514686950-8456209b5527?q=80&w=2070&auto=format&fit=crop" 
                    alt="Beaumont Tower" 
                    className="absolute inset-0 w-full h-full object-cover"
                />
                 {/* Green Overlay */}
                <div className="absolute inset-0 bg-[#18453B]/70 mix-blend-multiply"></div>

                <div className="absolute top-1/2 -translate-y-1/2 left-0 w-full px-8 md:px-12 text-center">
                     <div className="max-w-4xl mx-auto text-white">
                         <h1 className="text-6xl md:text-9xl font-black mb-6 uppercase italic leading-none">
                             Spartans Will.
                         </h1>
                         <p className="text-xl md:text-2xl mb-12 font-medium">
                             We are working to solve the world's most challenging problems.
                         </p>
                         <Button className="bg-white text-[#18453B] hover:bg-gray-100 font-bold uppercase tracking-widest px-10 h-14 rounded-sm shadow-xl">
                             Discover {schoolName.split(' ')[0]}
                         </Button>
                     </div>
                </div>
            </header>

            {/* Stats / Impact */}
            <section className="bg-white py-24 px-6 md:px-12">
                <div className="max-w-[1240px] mx-auto grid grid-cols-1 md:grid-cols-3 gap-12 text-center text-[#18453B]">
                     <div className="p-8 border-t-2 border-[#18453B] bg-gray-50">
                         <Sprout size={48} className="mx-auto mb-6" />
                         <h3 className="text-2xl font-bold mb-4">Pioneer</h3>
                         <p className="text-gray-600">First Land-Grant Institution</p>
                     </div>
                      <div className="p-8 border-t-2 border-[#18453B] bg-gray-50">
                         <Globe size={48} className="mx-auto mb-6" />
                         <h3 className="text-2xl font-bold mb-4">Global</h3>
                         <p className="text-gray-600">Top 100 Global University</p>
                     </div>
                      <div className="p-8 border-t-2 border-[#18453B] bg-gray-50">
                         <Users size={48} className="mx-auto mb-6" />
                         <h3 className="text-2xl font-bold mb-4">Community</h3>
                         <p className="text-gray-600">50,000+ Students Strong</p>
                     </div>
                </div>
            </section>

             {/* News Blocks */}
             <section className="bg-[#f2f2f2] py-24 px-6 md:px-12">
                 <div className="max-w-[1240px] mx-auto">
                     <div className="flex justify-between items-end mb-12 border-b-2 border-[#18453B] pb-4">
                         <h2 className="text-3xl font-black text-[#18453B] uppercase">{schoolName} Today</h2>
                         <a href="#" className="font-bold text-[#18453B] text-xs uppercase hover:underline mb-2">More News</a>
                     </div>

                     <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
                         {[
                             "MSU scientists discover new method for carbon capture",
                             "College of Education launches new urban teaching program",
                             "Spartan athletics announces facility upgrades"
                         ].map((title, i) => (
                             <div key={i} className="bg-white p-6 shadow-sm group cursor-pointer hover:-translate-y-1 transition-transform duration-300">
                                 <div className="h-48 bg-gray-200 mb-6 overflow-hidden">
                                     <img src={`https://source.unsplash.com/random/400x300?sig=${i+120}`} className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500" alt="News"/>
                                 </div>
                                 <h3 className="text-xl font-bold text-[#18453B] mb-4 leading-tight">{title}</h3>
                                  <div className="flex items-center gap-2 text-[#18453B] font-bold uppercase text-xs tracking-widest group-hover:gap-3 transition-all">
                                     Read More <ArrowRight size={14}/>
                                 </div>
                             </div>
                         ))}
                     </div>
                 </div>
             </section>

             {/* Footer */}
             <footer className="bg-[#18453B] text-white py-16 px-6 md:px-12">
                  <div className="max-w-[1240px] mx-auto flex flex-col md:flex-row justify-between gap-12 font-sans text-sm">
                      <div className="max-w-xs">
                           <span className="font-serif font-black text-2xl text-white mb-6 block uppercase tracking-wide">{schoolName}</span>
                          <p className="mb-6 opacity-80">
                              East Lansing, MI 48824 <br/>
                              (517) 355-1855
                          </p>
                      </div>

                      <div className="grid grid-cols-2 lg:grid-cols-4 gap-8 font-bold uppercase text-xs tracking-widest opacity-80">
                          <ul className="space-y-4 hover:[&_a]:text-white">
                              <li><a href="#">Privacy</a></li>
                              <li><a href="#">Accessibility</a></li>
                              <li><a href="#">Site Map</a></li>
                          </ul>
                          <ul className="space-y-4 hover:[&_a]:text-white">
                               <li><a href="#">Call MSU</a></li>
                              <li><a href="#">Contact</a></li>
                              <li><a href="#">Visit</a></li>
                          </ul>
                      </div>
                  </div>
                  <div className="max-w-[1240px] mx-auto mt-16 text-center text-xs text-white/50 border-t border-white/20 pt-8">
                      © 2026 {schoolName} Board of Trustees
                  </div>
             </footer>
        </div>
    );
};

export default MichiganState;
